// ============================================================
// Login / sign-up gate (Better Auth client + HTML overlay)
// ============================================================

/// <reference types="vite/client" />

import { createAuthClient } from 'better-auth/client';

export type AuthUser = {
  id: string;
  name: string;
  email: string;
};

const AUTH_BOOTSTRAP_KEY = 'lr-auth-bootstrap';
const AUTH_BOOTSTRAP_MAX_MS = 5 * 60 * 1000;

const authClient = createAuthClient({
  /** Explicit origin so auth calls always match the page (avoids wrong default behind some proxies). */
  ...(typeof window !== 'undefined' ? { baseURL: window.location.origin } : {}),
  fetchOptions: { credentials: 'include' },
});

function readAuthBootstrap(): AuthUser | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(AUTH_BOOTSTRAP_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as { id?: string; name?: string; email?: string; t?: number };
    if (!o.id || !o.email || typeof o.t !== 'number') return null;
    if (Date.now() - o.t > AUTH_BOOTSTRAP_MAX_MS) {
      sessionStorage.removeItem(AUTH_BOOTSTRAP_KEY);
      return null;
    }
    return { id: o.id, name: typeof o.name === 'string' ? o.name : '', email: o.email };
  } catch {
    return null;
  }
}

function setAuthBootstrap(user: AuthUser): void {
  try {
    sessionStorage.setItem(
      AUTH_BOOTSTRAP_KEY,
      JSON.stringify({ id: user.id, name: user.name, email: user.email, t: Date.now() }),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Clears tab-local post-login bridge (e.g. after sign-out). */
export function clearAuthBootstrap(): void {
  try {
    sessionStorage.removeItem(AUTH_BOOTSTRAP_KEY);
  } catch {
    /* ignore */
  }
}

/** Normalizes a Better Auth–style user record to `AuthUser`. */
function authUserFromRecord(user: Record<string, unknown>): AuthUser | null {
  const rawId = user.id;
  const id =
    typeof rawId === 'string'
      ? rawId
      : rawId != null && (typeof rawId === 'number' || typeof rawId === 'bigint')
        ? String(rawId)
        : '';
  const rawEmail = user.email;
  const email = typeof rawEmail === 'string' ? rawEmail : rawEmail != null ? String(rawEmail) : '';
  const name = typeof user.name === 'string' ? user.name : '';
  if (!id || !email) return null;
  return { id, name, email };
}

/**
 * Parses `user` from a Better Auth client `data` payload (sign-in / sign-up / get-session).
 * Handles several shapes: `{ user }`, `{ session: { user } }`, or `data` being the user object.
 */
function userFromAuthData(data: unknown): AuthUser | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;

  const direct = authUserFromRecord(d);
  if (direct) return direct;

  const top = d.user;
  if (top && typeof top === 'object') {
    const u = authUserFromRecord(top as Record<string, unknown>);
    if (u) return u;
  }

  const session = d.session;
  if (session && typeof session === 'object') {
    const s = session as Record<string, unknown>;
    const nested = s.user;
    if (nested && typeof nested === 'object') {
      const u = authUserFromRecord(nested as Record<string, unknown>);
      if (u) return u;
    }
  }

  return null;
}

function userFromAuthResult(result: unknown): AuthUser | null {
  if (!result || typeof result !== 'object') return null;
  const r = result as Record<string, unknown>;
  const wrapped = userFromAuthData(r.data);
  if (wrapped) return wrapped;
  return userFromAuthData(result);
}

async function readSessionUser(): Promise<AuthUser | null> {
  const res = (await authClient.getSession()) as {
    data?: unknown;
    error?: unknown;
  };
  return userFromAuthData(res.data);
}

/**
 * Same endpoint `UIManager` uses — JSON body often has top-level `user` while the Better Auth
 * client wraps differently; useful when `getSession()` does not deserialize as expected.
 */
async function readSessionFromHttpGetSession(): Promise<AuthUser | null> {
  try {
    const r = await fetch('/api/auth/get-session', { credentials: 'include' });
    if (!r.ok) return null;
    const body: unknown = await r.json();
    return userFromAuthData(body) ?? userFromAuthData((body as Record<string, unknown>).data);
  } catch {
    return null;
  }
}

/** After sign-in cookie is set, the next `get-session` can briefly return null — retry a few times. */
async function readSessionUserWithRetry(
  attempts = 8,
  delayMs = 150,
): Promise<AuthUser | null> {
  for (let i = 0; i < attempts; i++) {
    const u = await readSessionUser();
    if (u) return u;
    if (i < attempts - 1) await new Promise(r => setTimeout(r, delayMs));
  }
  return null;
}

/**
 * True when the latest `/api/me` answered 503 `APPROVAL_CHECK_FAILED`: the server could not run its
 * approval check and failed closed. Nothing is known to be wrong with the account.
 */
let approvalCheckUnavailable = false;

/** Same-origin session check used elsewhere (`friends.html`); good fallback if the auth client shape differs. */
async function readAuthUserViaMe(): Promise<AuthUser | null> {
  try {
    const r = await fetch('/api/me', { credentials: 'include' });
    approvalCheckUnavailable = false;
    if (r.status === 403) {
      // Live session but the account is pending / rejected: not signed in, and the tab-local
      // bootstrap must not keep routing this tab as if it were.
      clearAuthBootstrap();
      return null;
    }
    if (r.status === 503) {
      // Temporarily unavailable — try again. Deliberately does NOT clear the bootstrap: the
      // account may be fine, only the check is down.
      const body = (await r.json().catch(() => null)) as Record<string, unknown> | null;
      const nested =
        body && body.error && typeof body.error === 'object'
          ? (body.error as Record<string, unknown>)
          : null;
      const code = body && typeof body.code === 'string' ? body.code : nested?.code;
      approvalCheckUnavailable = code === 'APPROVAL_CHECK_FAILED';
      return null;
    }
    if (!r.ok) return null;
    const d = (await r.json()) as { user?: Record<string, unknown> };
    const u = d.user;
    if (!u || typeof u !== 'object') return null;
    return authUserFromRecord(u);
  } catch {
    return null;
  }
}

/**
 * Session for routing (`/game`, `/login`, landing). Prefer this over `readSessionUser()` alone:
 * `authClient.getSession()` can return an empty payload while the session cookie still works for `/api/me`.
 */
async function resolveAuthUserForRouting(): Promise<AuthUser | null> {
  const tryNetwork = async (): Promise<AuthUser | null> => {
    let u = await readSessionUser();
    if (u) return u;
    u = await readSessionFromHttpGetSession();
    if (u) return u;
    u = await readAuthUserViaMe();
    if (u) return u;
    // Brief wait: full-page navigation from `/login` can land before the cookie is visible to the next requests.
    await new Promise(r => setTimeout(r, 120));
    u = await readSessionUser();
    if (u) return u;
    u = await readSessionFromHttpGetSession();
    if (u) return u;
    return readAuthUserViaMe();
  };

  const u = await tryNetwork();
  if (u) {
    clearAuthBootstrap();
    return u;
  }
  const boot = readAuthBootstrap();
  if (boot) return boot;
  return null;
}

/** Current Better Auth session, or null (no UI). */
export async function getAuthUserOrNull(): Promise<AuthUser | null> {
  return resolveAuthUserForRouting();
}

/** Clears the Better Auth session cookie (same origin as `/api/auth`). */
export async function signOutUser(): Promise<{ ok: boolean; message?: string }> {
  try {
    const r = (await authClient.signOut()) as { error?: unknown };
    if (r && typeof r === 'object' && 'error' in r && r.error) {
      return { ok: false, message: formatAuthClientError(r, 'Sign out failed') };
    }
    clearAuthBootstrap();
    return { ok: true };
  } catch (e) {
    return { ok: false, message: (e as Error).message || 'Sign out failed' };
  }
}

function setErr(elRoot: HTMLElement, msg: string) {
  const el = elRoot.querySelector('#auth-err');
  if (el) el.textContent = msg;
}

/** Sign-up approval gate: server error codes → player-facing copy. */
const AUTH_CODE_MESSAGES: Record<string, string> = {
  ACCOUNT_PENDING_APPROVAL:
    "Your account is awaiting approval. You'll be able to sign in once an admin approves it.",
  ACCOUNT_REJECTED: 'Your account request was not approved.',
  SIGNUPS_CLOSED: "LoveRift isn't accepting new accounts right now.",
  // 503: the server could not run its approval check and fails closed.
  APPROVAL_CHECK_FAILED: 'Sign-in is temporarily unavailable. Please try again in a moment.',
  // 403 from Better Auth's sign-in when the server enforces email verification
  // (REQUIRE_EMAIL_VERIFICATION); that sign-in attempt also re-sends the link.
  EMAIL_NOT_VERIFIED:
    "Your email isn't confirmed yet. We've emailed you a confirmation link — open it (check spam too), then sign in again.",
};

const SIGNUP_REQUEST_RECEIVED =
  "Thanks — your request was received. Your account is awaiting approval; you'll be able to sign in once an admin approves it. If you already have an account with this email, sign in instead.";

const SIGNUP_VERIFY_EMAIL =
  "One more step — we've emailed you a confirmation link. Open it to prove the address is yours (check spam too), then come back and sign in. If you already have an account with this email, sign in instead.";

/** Where the emailed confirmation link lands once Better Auth has verified it (login.html handles `verified=1`). */
const VERIFY_CALLBACK_URL = '/login.html?verified=1';

function authResultErrorCode(result: unknown): string {
  if (!result || typeof result !== 'object') return '';
  const err = (result as { error?: unknown }).error;
  if (!err || typeof err !== 'object') return '';
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

type SignupMode = 'open' | 'approval' | 'closed';

type SiteAuthConfig = {
  signupMode: SignupMode;
  /** `null` = the server didn't say (older server, or the request failed). */
  emailVerificationRequired: boolean | null;
};

/** From `/api/site/config`; a missing `signupMode` or an unreachable server counts as 'approval'. */
async function fetchSiteAuthConfig(): Promise<SiteAuthConfig> {
  const unknown: SiteAuthConfig = { signupMode: 'approval', emailVerificationRequired: null };
  try {
    const r = await fetch('/api/site/config');
    if (!r.ok) return unknown;
    const d = (await r.json()) as { signupMode?: unknown; emailVerificationRequired?: unknown };
    return {
      signupMode: d.signupMode === 'open' || d.signupMode === 'closed' ? d.signupMode : 'approval',
      emailVerificationRequired:
        typeof d.emailVerificationRequired === 'boolean' ? d.emailVerificationRequired : null,
    };
  } catch {
    return unknown;
  }
}

/** Better Auth client often returns `error` without a human `message` — surface something useful. */
function formatAuthClientError(result: unknown, fallback: string): string {
  if (!result || typeof result !== 'object') return fallback;
  const r = result as Record<string, unknown>;
  const err = r.error;
  if (!err || typeof err !== 'object') {
    if (import.meta.env.DEV && r.data == null && !r.error) {
      return `${fallback} (no error payload — check Network tab for /api/auth/sign-up/email)`;
    }
    return fallback;
  }
  const e = err as Record<string, unknown>;
  // Approval-gate codes first: they arrive as 403s (503 for APPROVAL_CHECK_FAILED), which the
  // generic "CORS" / "Server error during sign-up" branches below would mislabel.
  const nested = e.error && typeof e.error === 'object' ? (e.error as Record<string, unknown>) : null;
  const code =
    typeof e.code === 'string' ? e.code : nested && typeof nested.code === 'string' ? nested.code : '';
  if (code && Object.prototype.hasOwnProperty.call(AUTH_CODE_MESSAGES, code)) {
    return AUTH_CODE_MESSAGES[code];
  }
  const msg = typeof e.message === 'string' ? e.message.trim() : '';
  if (msg) return msg;
  const status = typeof e.status === 'number' ? e.status : null;
  if (status === 403 || status === 401) {
    return 'Request blocked (CORS or origin). Ensure TRUSTED_ORIGINS includes this page URL and restart the server.';
  }
  if (status != null && status >= 500) {
    return 'Server error during sign-up. Is Postgres running and DATABASE_URL correct? Check server terminal logs.';
  }
  if (typeof e.code === 'string' && e.code) return e.code;
  if (status != null) return `Request failed (HTTP ${status}).`;
  return fallback;
}

/** Which auth tab to open from `?tab=up` (Play Free → create account first). */
export function initialAuthTabFromUrl(): 'in' | 'up' {
  const tab = new URLSearchParams(window.location.search).get('tab')?.toLowerCase();
  if (tab === 'up' || tab === 'signup' || tab === 'register' || tab === 'create') return 'up';
  return 'in';
}

/** Renders sign-in / sign-up UI immediately (do not await network first). */
export function mountAuthScreen(initialTab: 'in' | 'up' = initialAuthTabFromUrl()): HTMLElement {
  const root = document.getElementById('auth-screen');
  if (!root) {
    throw new Error('Missing #auth-screen in index.html');
  }

  root.style.display = 'flex';

  root.innerHTML = `
    <div class="auth-card">
      <div class="auth-brand"><span class="love">Love</span><span class="rift">Rift</span></div>
      <p class="auth-sub">Your World. Your Desire.</p>
      <div id="auth-tabs" class="auth-tabs">
        <button type="button" class="auth-tab active" data-tab="in">Sign In</button>
        <button type="button" class="auth-tab" data-tab="up">Create Account</button>
      </div>
      <div id="auth-err" class="auth-err" role="alert"></div>
      <form id="form-signin" class="auth-form">
        <label>Email<input type="email" name="email" required autocomplete="username" placeholder="you@example.com" /></label>
        <label>Password<input type="password" name="password" required minlength="8" autocomplete="current-password" placeholder="••••••••" /></label>
        <button type="submit" class="auth-submit">Enter the Rift</button>
      </form>
      <form id="form-signup" class="auth-form hidden">
        <label>Display Name<input type="text" name="name" required maxlength="32" autocomplete="nickname" placeholder="Your name in the Rift" /></label>
        <label>Email<input type="email" name="email" required autocomplete="username" placeholder="you@example.com" /></label>
        <label>Password<input type="password" name="password" required minlength="8" autocomplete="new-password" placeholder="Min. 8 characters" /></label>
        <label style="display:flex;gap:10px;align-items:flex-start;text-transform:none;letter-spacing:0;font-size:13px"><input type="checkbox" name="ageConfirmed" required style="width:16px;height:16px;margin:2px 0 0;flex:none" /><span>I'm 18 or older and I understand LoveRift is an adults-only world.</span></label>
        <button type="submit" class="auth-submit">Join LoveRift — Free</button>
      </form>
    </div>
  `;

  const tabIn = root.querySelector('[data-tab="in"]') as HTMLButtonElement;
  const tabUp = root.querySelector('[data-tab="up"]') as HTMLButtonElement;
  const formIn = root.querySelector('#form-signin') as HTMLFormElement;
  const formUp = root.querySelector('#form-signup') as HTMLFormElement;

  const showTab = (which: 'in' | 'up') => {
    setErr(root, '');
    const up = which === 'up';
    tabIn.classList.toggle('active', !up);
    tabUp.classList.toggle('active', up);
    formIn.classList.toggle('hidden', up);
    formUp.classList.toggle('hidden', !up);
  };

  tabIn.addEventListener('click', () => showTab('in'));
  tabUp.addEventListener('click', () => showTab('up'));
  showTab(initialTab);

  return root;
}

export async function ensureAuthenticatedUser(
  initialTab: 'in' | 'up' = initialAuthTabFromUrl(),
): Promise<AuthUser> {
  const root = mountAuthScreen(initialTab);

  const existing = await Promise.race([
    resolveAuthUserForRouting(),
    new Promise<AuthUser | null>((resolve) => {
      setTimeout(() => resolve(null), 4000);
    }),
  ]);
  if (existing) {
    root.style.display = 'none';
    return existing;
  }
  if (approvalCheckUnavailable) {
    // `/api/me` said 503 APPROVAL_CHECK_FAILED — say "try again" rather than a bare sign-in form.
    setErr(root, AUTH_CODE_MESSAGES.APPROVAL_CHECK_FAILED);
  }

  return new Promise(resolve => {
    const formIn = root.querySelector('#form-signin') as HTMLFormElement;
    const formUp = root.querySelector('#form-signup') as HTMLFormElement;

    const finishWithUser = async (hint?: AuthUser | null) => {
      let u = hint ?? null;
      if (!u) u = await readSessionUserWithRetry();
      if (!u) u = await readAuthUserViaMe();
      if (!u) {
        setErr(
          root,
          'Could not read session after login. Try again, or clear site data if this persists (cookie / origin settings).',
        );
        return;
      }
      setAuthBootstrap(u);
      root.style.display = 'none';
      resolve(u);
    };

    formIn.addEventListener('submit', async ev => {
      ev.preventDefault();
      setErr(root, '');
      const fd = new FormData(formIn);
      const email = String(fd.get('email') || '').trim();
      const password = String(fd.get('password') || '');
      const r = await authClient.signIn.email({ email, password });
      if (r && typeof r === 'object' && 'error' in r && (r as { error?: unknown }).error) {
        setErr(root, formatAuthClientError(r, 'Sign in failed'));
        return;
      }
      const fromSignIn = userFromAuthResult(r);
      await finishWithUser(fromSignIn);
    });

    formUp.addEventListener('submit', async ev => {
      ev.preventDefault();
      setErr(root, '');
      const fd = new FormData(formUp);
      const name = String(fd.get('name') || '').trim();
      const email = String(fd.get('email') || '').trim();
      const password = String(fd.get('password') || '');
      // Adults only: the server requires the same confirmation (server/src/ageGate.ts).
      const ageConfirmed = fd.get('ageConfirmed') === 'on';
      if (!ageConfirmed) {
        setErr(root, 'Please confirm you are 18 or older.');
        return;
      }
      // callbackURL: where the confirmation link lands IF the server sends one. (Not passed to
      // signIn below — the auth client would follow it as a redirect after a successful sign-in.)
      const signUpBody = { name, email, password, callbackURL: VERIFY_CALLBACK_URL, ageConfirmed };
      const r = await authClient.signUp.email(signUpBody as Parameters<typeof authClient.signUp.email>[0]);
      if (r && typeof r === 'object' && 'error' in r && (r as { error?: unknown }).error) {
        setErr(root, formatAuthClientError(r, 'Sign up failed'));
        return;
      }
      // Never finish from the sign-up response: it carries a `user` even for an account that is
      // still pending approval (and for a duplicate email), and it never sets a session.
      const config = await fetchSiteAuthConfig();
      if (config.signupMode !== 'open') {
        // The account is pending approval, so a sign-in here could only be refused (a 403 in the
        // admin error log for every sign-up). `/api/site/config` says whether a confirmation
        // email went out; when it doesn't say, keep the usual "request received" copy.
        formUp.reset();
        setErr(
          root,
          config.emailVerificationRequired === true ? SIGNUP_VERIFY_EMAIL : SIGNUP_REQUEST_RECEIVED,
        );
        return;
      }
      const signedIn = await authClient.signIn.email({ email, password });
      if (
        signedIn &&
        typeof signedIn === 'object' &&
        'error' in signedIn &&
        (signedIn as { error?: unknown }).error
      ) {
        if (authResultErrorCode(signedIn) === 'EMAIL_NOT_VERIFIED') {
          formUp.reset();
          setErr(root, SIGNUP_VERIFY_EMAIL);
          return;
        }
        setErr(root, formatAuthClientError(signedIn, 'Sign in failed'));
        return;
      }
      await finishWithUser(userFromAuthResult(signedIn));
    });

  });
}
